/**
 * @file AutoCorsProvider.js
 * @description Proveedor automático de solución CORS para integración con React
 * @version 1.0.0
 * @author Nubelytics Engineering Team
 */

import React, { useEffect, useState, createContext, useContext } from 'react';
import corsProxyRegistry, { initCorsProxy } from './CorsProxyRegistry';
import corsDiagnosticTool from './CorsDiagnosticTool';
import corsProxyConfig from './CorsProxyConfig';
import corsTelemetry from './CorsTelemetry';

// Contexto para la gestión CORS a nivel de aplicación
const CorsContext = createContext({
  status: 'initializing',
  diagnostics: null,
  addDomain: () => {},
  removeDomain: () => {},
  runDiagnostic: () => Promise.resolve([]),
  forcedDomains: []
});

/**
 * Hook para acceder al contexto CORS desde cualquier componente
 * @returns {Object} Contexto CORS con métodos y estado
 */
export const useCors = () => useContext(CorsContext);

/**
 * Lista de dominios problemáticos conocidos
 * Estos dominios siempre usarán el proxy CORS
 */
const KNOWN_PROBLEMATIC_DOMAINS = [
  'api.mercadolibre.com',
  'api.mercadopago.com',
  'api-3t.sandbox.paypal.com',
  'api-m.sandbox.paypal.com',
  'api.sandbox.paypal.com',
  'api-3t.paypal.com',
  'api-m.paypal.com',
  'api.paypal.com'
];

/**
 * Componente proveedor de solución CORS
 * Inicializa automáticamente el sistema de manejo CORS y proporciona
 * contexto para administración y diagnóstico desde cualquier parte de la aplicación
 */
export const AutoCorsProvider = ({ children }) => {
  // Estado para seguimiento de la inicialización
  const [status, setStatus] = useState('initializing');
  const [diagnostics, setDiagnostics] = useState(null);
  const [forcedDomains, setForcedDomains] = useState([]);
  
  // Función para actualizar la lista de dominios forzados
  const refreshForcedDomains = () => {
    setForcedDomains([...corsProxyConfig.ALWAYS_USE_PROXY_DOMAINS]);
  };
  
  // Inicialización del sistema CORS
  useEffect(() => {
    const initialize = async () => {
      try {
        console.info('[AutoCorsProvider] Inicializando sistema CORS...');
        
        // Paso 1: Añadir dominios problemáticos conocidos
        let domainsAdded = 0;
        for (const domain of KNOWN_PROBLEMATIC_DOMAINS) {
          if (corsProxyConfig.addDomainToForceList(domain)) {
            domainsAdded++;
          }
        }
        console.info(`[AutoCorsProvider] Añadidos ${domainsAdded} dominios problemáticos a la lista de proxy`);
        
        // Paso 2: Inicializar el sistema CORS
        initCorsProxy();
        
        // Paso 3: Autodetección de dominios problemáticos basada en localStorage
        setTimeout(() => {
          try {
            const corsErrors = localStorage.getItem('nubelytics_cors_errors');
            if (corsErrors) {
              const errors = JSON.parse(corsErrors);
              
              // Analizar errores recientes (última semana)
              const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
              const recentErrors = errors.filter(err => err.timestamp > oneWeekAgo);
              
              // Agrupar por dominio
              const domainCounts = {};
              for (const error of recentErrors) {
                if (error.domain) {
                  domainCounts[error.domain] = (domainCounts[error.domain] || 0) + 1;
                }
              }
              
              // Si un dominio tiene más de 3 errores, añadirlo a la lista de forzado
              let autoAddedDomains = 0;
              for (const [domain, count] of Object.entries(domainCounts)) {
                if (count >= 3) {
                  if (corsProxyConfig.addDomainToForceList(domain)) {
                    autoAddedDomains++;
                    corsTelemetry.trackEvent('auto_add_domain', { domain, errorCount: count });
                  }
                }
              }
              
              if (autoAddedDomains > 0) {
                console.info(`[AutoCorsProvider] Auto-añadidos ${autoAddedDomains} dominios problemáticos`);
                refreshForcedDomains();
              }
            }
          } catch (error) {
            console.error('[AutoCorsProvider] Error en autodetección:', error);
          }
        }, 3000);
        
        // Paso 4: Ejecutar diagnóstico inicial en background
        setTimeout(async () => {
          try {
            const results = await corsDiagnosticTool.runFullDiagnosis();
            setDiagnostics(results);
            
            // Analizar resultados para añadir dominios problemáticos
            const resultsArray = Array.isArray(results) ? results : [];
            const problematicDomains = resultsArray
              .filter(result => 
                !result.options?.corsCheck?.valid || 
                !result.direct?.success)
              .map(result => {
                try {
                  return new URL(result.url).hostname;
                } catch (e) {
                  return null;
                }
              })
              .filter(Boolean);
            
            // Añadir dominios problemáticos detectados
            let diagAddedDomains = 0;
            for (const domain of problematicDomains) {
              if (corsProxyConfig.addDomainToForceList(domain)) {
                diagAddedDomains++;
                corsTelemetry.trackEvent('diag_add_domain', { domain });
              }
            }
            
            if (diagAddedDomains > 0) {
              console.info(`[AutoCorsProvider] Añadidos ${diagAddedDomains} dominios problemáticos desde diagnóstico`);
              refreshForcedDomains();
            }
          } catch (error) {
            console.error('[AutoCorsProvider] Error en diagnóstico inicial:', error);
          }
        }, 5000);
        
        // Actualizar estado
        setStatus('ready');
        refreshForcedDomains();
        
        // Registrar evento de telemetría
        corsTelemetry.trackEvent('cors_system_initialized', {
          timestamp: Date.now(),
          appVersion: process.env.REACT_APP_VERSION
        });
      } catch (error) {
        console.error('[AutoCorsProvider] Error de inicialización:', error);
        setStatus('error');
        
        // Registrar error en telemetría
        corsTelemetry.trackEvent('cors_system_error', {
          error: error.message,
          stack: error.stack
        });
      }
    };
    
    initialize();
    
    // Limpieza al desmontar
    return () => {
      // Este es un componente raíz que normalmente no se desmonta,
      // pero por si acaso, liberamos recursos
    };
  }, []);
  
  // Función para añadir un dominio a la lista forzada
  const addDomain = (domain) => {
    if (corsProxyConfig.addDomainToForceList(domain)) {
      refreshForcedDomains();
      corsTelemetry.trackEvent('manual_add_domain', { domain });
      return true;
    }
    return false;
  };
  
  // Función para eliminar un dominio de la lista forzada
  const removeDomain = (domain) => {
    if (corsProxyConfig.removeDomainFromForceList(domain)) {
      refreshForcedDomains();
      corsTelemetry.trackEvent('remove_domain', { domain });
      return true;
    }
    return false;
  };
  
  // Función para ejecutar diagnóstico
  const runDiagnostic = async () => {
    try {
      const results = await corsDiagnosticTool.runFullDiagnosis();
      setDiagnostics(results);
      return results;
    } catch (error) {
      console.error('[AutoCorsProvider] Error ejecutando diagnóstico:', error);
      return [];
    }
  };
  
  // Valor del contexto
  const contextValue = {
    status,
    diagnostics,
    addDomain,
    removeDomain,
    runDiagnostic,
    forcedDomains,
    telemetry: corsTelemetry.getMetricsSummary()
  };
  
  return (
    <CorsContext.Provider value={contextValue}>
      {children}
    </CorsContext.Provider>
  );
};

/**
 * Componente de alto orden (HOC) para envolver la aplicación con el proveedor CORS
 * @param {React.Component} Component - Componente a envolver
 * @returns {React.Component} Componente envuelto con proveedor CORS
 */
export const withCorsProvider = (Component) => {
  const WithCorsProvider = (props) => (
    <AutoCorsProvider>
      <Component {...props} />
    </AutoCorsProvider>
  );
  
  WithCorsProvider.displayName = `WithCorsProvider(${Component.displayName || Component.name || 'Component'})`;
  return WithCorsProvider;
};

/**
 * APIs utilitarias para configuración en caliente de dominios problemáticos.
 * Permite invocar AutoCorsProvider.addProblemDomain(...) incluso antes de que
 * el componente haya sido montado.
 */
AutoCorsProvider.addProblemDomain = function(domain) {
  if (!domain || typeof domain !== 'string') return false;
  return corsProxyConfig.addDomainToForceList(domain);
};

/**
 * Añade un conjunto de dominios problemáticos de una sola vez.
 * @param {string[]} domains
 * @returns {number} cantidad de dominios añadidos
 */
AutoCorsProvider.addProblemDomains = function(domains = []) {
  if (!Array.isArray(domains)) return 0;
  let count = 0;
  domains.forEach((d) => {
    if (AutoCorsProvider.addProblemDomain(d)) count += 1;
  });
  return count;
};

/**
 * Helper para consultar si una URL determinada debería ser proxied.
 * @param {string} url
 * @returns {boolean}
 */
AutoCorsProvider.needsProxy = function(url) {
  return corsProxyConfig.shouldAlwaysUseProxy(url);
};

export default AutoCorsProvider;
